import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaRocket, FaImage, FaLinkedin, FaFacebook } from 'react-icons/fa';

interface MarketingToolProps {
  title: string;
  description: string;
  icon: JSX.Element;
  color: string;
  comingSoon?: boolean;
  onClick?: () => void;
}

const MarketingToolCard: React.FC<MarketingToolProps> = ({ 
  title, 
  description, 
  icon, 
  color,
  comingSoon = false, 
  onClick 
}) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <div 
      className={`relative transform transition-all duration-300 ${comingSoon ? 'opacity-70' : 'hover:-translate-y-2 cursor-pointer'}`}
      onClick={comingSoon ? undefined : onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Card with glass morphism */}
      <div className="
        relative overflow-hidden rounded-xl bg-white/20 backdrop-blur-lg 
        border border-white/30 shadow-lg group transition-all duration-300
        hover:shadow-xl p-6 h-full
      ">
        {/* Gradient background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${color} opacity-10 group-hover:opacity-20 transition-opacity`}></div>
        
        {/* Icon */}
        <div className={`
          w-12 h-12 rounded-lg mb-4 flex items-center justify-center text-white 
          bg-gradient-to-br ${color} shadow-lg group-hover:scale-110 transition-transform
        `}>
          <span className="text-xl">{icon}</span>
        </div>
        
        {/* Content */}
        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-blue-600 transition-colors">
          {title}
          {comingSoon && (
            <span className="ml-2 px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">
              Coming Soon
            </span>
          )}
        </h3>
        <p className="text-gray-600 group-hover:text-gray-800 transition-colors">
          {description}
        </p>
        
        {/* Hidden action button that appears on hover */}
        {!comingSoon && (
          <div className="
            absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 
            transition-opacity transform group-hover:translate-x-0 translate-x-4
          ">
            <button className="
              flex items-center justify-center w-8 h-8 rounded-full 
              bg-white/80 text-blue-600 shadow-md hover:bg-white transition-colors
            ">
              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

const Marketing: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);

  // Redirect if not logged in
  React.useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  if (!user) {
    return <div className="p-8 text-center">Loading...</div>;
  }

  return (
    <section className="py-24 relative overflow-hidden">
      {/* Background with gradient and mesh */}
      <div className="absolute inset-0 bg-gray-50 z-0">
        <div className="absolute inset-0 opacity-30 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIiBmaWxsPSJub25lIj48cGF0aCBkPSJNLjUuNWg2MHY2MEguNVYuNXptMzAgMTVhMTUgMTUgMCAwIDAtMTUgMTUgMTUgMTUgMCAwIDAgMTUgMTUgMTUgMTUgMCAwIDAgMTUtMTUgMTUgMTUgMCAwIDAtMTUtMTV6bTAgMGExNSAxNSAwIDAgMC0xNSAxNSAxNSAxNSAwIDAgMCAxNSAxNSAxNSAxNSAwIDAgMCAxNS0xNSAxNSAxNSAwIDAgMC0xNS0xNXoiIHN0cm9rZT0iIzIwMjAyMCIgc3Ryb2tlLW9wYWNpdHk9Ii4wNiIvPjwvc3ZnPg==')]"></div>
      </div>
      
      {/* Main content */}
      <div className="container relative z-10 mx-auto px-4">
        {/* Decorative elements */}
        <div className="absolute top-20 left-12 w-64 h-64 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-4000"></div>
        <div className="absolute -bottom-32 right-12 w-64 h-64 bg-pink-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
      
        {/* Section Title with 3D effect */}
        <div className="relative mb-16 text-center">
          <h2 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600 inline-block mb-2">
            Marketing Tools
          </h2>
          <div className="h-1 w-40 mx-auto rounded-full bg-gradient-to-r from-blue-400 to-purple-500"></div>
          <p className="mt-4 text-xl text-gray-600 max-w-2xl mx-auto">Powerful tools to help you market your business effectively</p>
        </div>
      
        {/* Tools Grid with 3D Cards and Glass Morphism */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          <MarketingToolCard
            title="Business Name Generator"
            description="Generate creative and memorable business names based on your industry, values, and target audience."
            icon={<FaRocket />}
            color="from-blue-400 to-indigo-500"
            onClick={() => navigate('/marketing/business-name-generator')}
          />
          
          <MarketingToolCard
            title="Logo Generator"
            description="Create professional logos for your business with customizable templates and design elements."
            icon={<FaImage />}
            color="from-indigo-400 to-blue-500"
            onClick={() => navigate('/marketing/logo-generator')}
          />
          
          <MarketingToolCard
            title="LinkedIn Profile Enhancer"
            description="Optimize your LinkedIn profile with AI-generated professional summaries, headlines, and experience descriptions."
            icon={<FaLinkedin />}
            color="from-blue-500 to-blue-600"
            onClick={() => navigate('/marketing/linkedin-profile-enhancer')}
          />
          
          <MarketingToolCard
            title="LinkedIn Business Profile Enhancer"
            description="Optimize your company's LinkedIn page with AI-generated content that attracts clients and showcases your business."
            icon={<FaLinkedin />}
            color="from-blue-600 to-blue-700"
            onClick={() => navigate('/marketing/linkedin-business-profile-enhancer')}
          />
          
          <MarketingToolCard
            title="Social Media Post Generator"
            description="Create engaging social media content for multiple platforms including Facebook, LinkedIn, Twitter, and Reddit."
            icon={<FaFacebook />}
            color="from-purple-400 to-indigo-500"
            onClick={() => navigate('/marketing/social-post-generator')}
          />
          
          <MarketingToolCard
            title="Text to Image Generator"
            description="Create professional images for your social media and marketing materials using AI-powered image generation."
            icon={<FaImage />}
            color="from-pink-400 to-rose-500"
            onClick={() => navigate('/marketing/text-to-image-generator')}
          />
          
          <MarketingToolCard
            title="Press Release Generator"
            description="Generate professional AP-style press releases to announce company news, product launches, and other milestones."
            icon={<FaRocket />}
            color="from-amber-400 to-orange-500"
            onClick={() => navigate('/marketing/press-release-generator')}
          />
          
          <MarketingToolCard
            title="Blog Post Creator"
            description="Create engaging blog posts from a brief description to enhance your content marketing strategy."
            icon={<FaRocket />}
            color="from-green-400 to-teal-500"
            onClick={() => navigate('/marketing/blog-post-creator')}
          />
        </div>
      </div>
    </section>
  );
};

export default Marketing;
